/* eslint-disable max-lines -- project chat shell: scroll contract, transport wiring, and composer ref bridge */
import {
  FileDropSurface,
  type FileUploadRejection,
} from '@heidi/design-system/file-upload';
import { toast } from '@heidi/design-system/toast';
import type { CSATRating } from '@heidi/schema/ml-scribe';
import { cn, normalizePastedFile, useIsMobile, useLatestRef } from '@heidi/utils';
import { useQueryClient } from '@tanstack/react-query';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type ComponentType,
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
  type Ref,
  type RefObject,
} from 'react';
import { useIntl } from 'react-intl';

import type { TrackWorkRpaDesktopRequiredShown } from '../../../contracts/desktop-required.types';
import type { ChatItem } from '../../../contracts/harness.types';
import type { ProjectFeedbackRating } from '../../../contracts/projects/requests.types';
import {
  isRestrictedProjectDataScope,
  lockedComposerPatientFetchId,
  patientProfileDisplayName,
  resolveContextLock,
} from '../../../contracts/projects/work-composer-context-lock';
import type { WorkspaceSnapshotV1 } from '../../../contracts/projects/workspace.types';
import { mintTurnId, type TurnOperation } from '../../../contracts/turn-identity';
import type { WorkSurface } from '../../../contracts/work-home-composer-analytics.types';
import type {
  TrackProjectChatErrorOnce,
  TrackWorkExperienceFeedbackActioned,
} from '../../../contracts/work-launch-analytics.types';
import type { WorkMode } from '../../../contracts/work-mode';
import { DESKTOP_APP_DOWNLOAD_URL } from '../../../data';
import {
  createDeferredTurnOperation,
  createRefusedTurnOperation,
} from '../../../data/harness/turn-operation-registry';
import { useIsAuthenticated } from '../../../data/injected-auth';
import type { CreatedChatArtifactPorts } from '../../../data/projects/artifacts/collectCreatedChatArtifacts';
import { usePublishCreatedChatArtifacts } from '../../../data/projects/artifacts/createdChatArtifacts';
import {
  artifactFieldUpdateAtom,
  artifactUserRevisionAtom,
  clearComposerLinkedItemsAtom,
  composerLinkedItemsAtom,
  openArtifactAtom,
  setOpenArtifactAtom,
  setOpenArtifactSilentAtom,
  type ProjectArtifactSink,
} from '../../../data/projects/artifacts/state';
import { ContinueAfterConnectProvider } from '../../../data/projects/components/continueAfterConnect';
import { ProvisionalSubmitRejectedError } from '../../../data/projects/components/ProjectChat.helpers';
import { isVisibleUserMessage } from '../../../data/projects/components/projectChatMessage.helpers';
import { useSubmitProjectFeedback } from '../../../data/projects/hooks';
import type { useIngestComputerUseNotePushCandidatesCore } from '../../../data/projects/hooks/useIngestComputerUseNotePushCandidates';
import type { useProjectArtifactAutoOpen as useProjectArtifactAutoOpenCore } from '../../../data/projects/hooks/useProjectArtifactAutoOpen';
import { useProjectBackgroundRegistration } from '../../../data/projects/hooks/useProjectBackgroundRegistration';
import type { useProjectDesktopDelegation as useProjectDesktopDelegationCore } from '../../../data/projects/hooks/useProjectDesktopDelegation';
import { useProjectPendingApprovals } from '../../../data/projects/hooks/useProjectPendingApprovals';
import { useSteerPendingApprovals } from '../../../data/projects/hooks/useSteerPendingApprovals';
import { useWorkExperienceFeedbackTrigger } from '../../../data/projects/hooks/useWorkExperienceFeedbackTrigger';
import {
  buildComputerUseRetryPrompt,
  findLatestOnDemandDesktopRequired,
  resolveComputerUseComposerBanner,
} from '../../../data/projects/integrations/desktopRequired';
import {
  completedTurnHasFeedbackTarget,
  resolveCompletedTurnMessageId,
} from '../../../data/projects/lib/completedTurnFeedback';
import { handleTranscriptEngagement } from '../../../data/projects/lib/composerShellFocus';
import {
  shouldShowProjectFollowUpQuestions,
  shouldShowProjectReplyFeedback,
} from '../../../data/projects/lib/shouldShowProjectReplyChrome';
import {
  findTrailingEvidenceChildToolCallId,
  groupProjectTurns,
} from '../../../data/projects/lib/turnLifecycle';
import { pendingComposerPromptAtom } from '../../../data/projects/pendingComposerPrompt';
import { usePublishProjectChatSubmit } from '../../../data/projects/recording/usePublishProjectChatSubmit';
import type { SubmitProjectTurnArgs } from '../../../data/projects/submit-project-turn-args';
import { useOptimisticFirstTurn } from '../../../data/projects/useOptimisticFirstTurn';
import {
  useIsWorkEarlierResponseActionsEnabled,
  useIsWorkMobileHandoffCtaEnabled,
} from '../../../flags';
import { useWorkComposerSourcesSlot, type ComposerLayout } from '../../composer/Composer';
import type { WorkComposerSessionAppearance } from '../../composer/composer-host';
import type { WorkRunSpeed } from '../../composer/ProjectRunOptionsSelector.utils';
import { useProjectConversationTimeline } from '../hooks/useProjectConversationTimeline';
import type {
  ProjectsComposerHandle,
  ProjectsComposerSession,
} from '../hooks/useProjectsComposerChatSession';
import {
  getIsStartingSeededTurn,
  useProjectSeededFirstTurn,
} from '../hooks/useProjectSeededFirstTurn';
import type { usePublishNotePushApplyCore } from '../hooks/usePublishNotePushApply';
import { useWorkExperienceFeedbackSubmission } from '../hooks/useWorkExperienceFeedbackSubmission';
import { mergePickedFiles, PROJECT_ATTACHMENTS_ACCEPT_FORMATS } from '../lib/attachments';
import { SharedProjectBanner } from '../SharedProjectBanner';
import type { ProjectTransport } from '../transport/useProjectTransport';
import { WorkChatNotFound } from '../WorkChatNotFound';
import { ComputerUseNudgeCard } from './ComputerUseNudgeCard';
import {
  ContinueOnMobileCard,
  type ContinueOnMobileCardAction,
} from './mobile-handoff/continue-on-mobile-card';
import type { ProjectConversationTimeline as ProjectConversationTimelineView } from './ProjectConversationTimeline';
import type { ProjectMessageActionsProps } from './ProjectMessageActions';
import { ProjectReplyFollowUpQuestions } from './ProjectReplyFollowUpQuestions';
import type { ProjectReplyMessageActionsProps } from './ProjectReplyMessageActions';
import { EARLIER_RESPONSE_ACTIONS_VISIBILITY } from './ResponseActionsReveal';
import type { WorkSessionComposerProps } from './WorkSessionComposer.host';
import { YourResidentDrawer, type YourResidentDrawerHost } from './YourResidentDrawer';

// The backend doesn't echo ratings back, so the store remembers them for the tab:
// a rating survives its reply moving into a turn footer and the chat being left
// and reopened. A read-only atom gives each store its own Map; rows read it only
// as initial state on mount, so it is mutated in place.
const givenReplyRatingsAtom = atom(() => new Map<string, ProjectFeedbackRating>());

interface EarlierEvidenceReplyTurn {
  toolCallId: string;
  items: ChatItem[];
  nextTurnFirstItem: ChatItem;
}

// Completed turns whose reply is an Evidence child answer: their action row
// rides inline with the child, whose answer is the copy text (the parent turn
// has no prose to copy). Uses the timeline's own grouping so these are exactly
// the completed turns whose footer row this replaces.
const findEarlierEvidenceReplyTurns = (
  items: readonly ChatItem[]
): EarlierEvidenceReplyTurn[] => {
  const { turnGroups } = groupProjectTurns(items, isVisibleUserMessage);
  const turns: EarlierEvidenceReplyTurn[] = [];
  for (let index = 0; index < turnGroups.length - 1; index += 1) {
    const turnItems = turnGroups[index]!.items;
    const toolCallId = findTrailingEvidenceChildToolCallId(turnItems);
    if (toolCallId && completedTurnHasFeedbackTarget(turnItems)) {
      turns.push({
        toolCallId,
        items: turnItems,
        nextTurnFirstItem: turnGroups[index + 1]!.items[0]!,
      });
    }
  }
  return turns;
};

/** The live turn's stop affordance, mirrored up to an embedding host. */
export interface ProjectChatTurnControls {
  /** Stop the active turn — routed the same way the composer's Stop is. */
  stopTurn: () => void;
  /** A stop request is already in flight. */
  cancelling: boolean;
}

interface ProjectChatSharedProps {
  /** Used as the label written into the floating overlay status. */
  projectTitle: string;
  /** Mirrors the transport's turn-in-flight up to the embedder (e.g. the
   *  routines list's "Running" byline). Called with `false` on unmount. */
  onTurnInFlightChange?: (inFlight: boolean) => void;
  /** Hands the live turn's stop control up to the embedder, so a host header
   *  can carry its own Stop beside the composer's (the routines run pane).
   *  Called with `null` on unmount. Memoise it — it drives an effect. */
  onTurnControlsChange?: (controls: ProjectChatTurnControls | null) => void;
  /** Overrides where artifact "open" events go. Omit to use the default
   *  Work-home canvas atoms (`setOpenArtifactAtom` / `...SilentAtom`). */
  artifactSink?: ProjectArtifactSink;
  /**
   * Override the per-turn workspace snapshot sent on submit. Omit to use the
   * Work-home canvas (`openArtifactAtom`). Work-in-session passes a host
   * session/tab/recording projection instead.
   */
  workspace?: WorkspaceSnapshotV1;
  /** Rendered in the transcript when there's no project and nothing pending —
   *  the panel's empty-state hint (provisional mode only). */
  emptyState?: ReactNode;
  analyticsSurface?: WorkSurface;
  analyticsClinicalSessionId?: string;
  /**
   * Which commands must resolve a desktop target before their turn POST.
   * `computer-use-only` still shows the picker; only skill/CU drafts block send
   * when no computer is ready.
   */
  desktopTargetRequirement?: 'all-turns' | 'computer-use-only';
  /** The flag only, never the queue's contents. */
  onHasQueuedTurnsChange?: (hasQueuedTurns: boolean) => void;
  /** Seeds the composer without sending. Yields to the project-scoped
   *  `pendingComposerPromptAtom` hand-off. */
  prefilledPrompt?: string;
  /** Rendered at the top of the transcript scroll, above the timeline — lets
   *  an embedder carry its opening assistant turn into the chat (the routine
   *  walkthrough's intro), so the handoff reads as one conversation. */
  timelineLeadingContent?: ReactNode;
  /** Transcript body scale. Routine embeds use P1; every other chat keeps the
   * compact default. */
  transcriptTextScale?: 'default' | 'p1';
  /** One-shot composer prefill (example chips). Re-applying the same string
   *  is a no-op in the editor — callers that need a re-fire append ZWSP. */
  composerPrefill?: string;
  /** Hardcoded opening assistant turn rendered above the timeline. Pages pass
   *  a string so they do not import timeline blocks (Chat quarantine). */
  openingAssistantMessage?: string;
  /** Attaches the standard message-feedback controls (thumbs + copy) to the
   *  opening assistant turn. Used for New Chat's routed greeting, which is the
   *  first real timeline item rather than a hardcoded guided-create hand-off —
   *  never set for the guided-create intro, so that bare bubble is unchanged. */
  openingAssistantMessageFeedback?: boolean;
  /** Taller/softer top scroll fade (e.g. `h-16`) for embedders whose header
   *  sits directly above the transcript — text dissolves under it instead of
   *  clipping at a hard edge. */
  timelineTopFadeClassName?: string;
  /** Both scroll fades' color override (PAGE_FADE_TONE / SURFACE_FADE_TONE
   *  from ProjectChatScrollFades). This chat is transparent — the host that
   *  knows the actual backdrop should pick the tone. */
  timelineFadeColorClassName?: string;
  /** Painted behind the composer dock; the chat only provides the positioning
   *  context and the focus-within group. */
  composerBackdrop?: ReactNode;
  /** Page-owned control rendered in the composer toolbar. The chat does not
   *  interpret it — Work home uses this for Auto/Evidence. */
  composerToolbarLeading?: ReactNode;
  /** Host-owned Fast/Normal control for the composer + menu. */
  composerSpeedMenu?: { value: WorkRunSpeed; onChange: (next: WorkRunSpeed) => void };
  composerMode?: WorkMode;
  onComposerModeChange?: (next: WorkMode) => void;
  composerPlaceholderPreview?: string | null;
  composerSessionAppearance?: WorkComposerSessionAppearance;
  composerLayout?: ComposerLayout;
  toolbarSourcesSlot?: ReactNode;
  /** Sources control the composer shows while in Evidence only. */
  composerEvidenceSourcesSlot?: ReactNode;
  /**
   * Freeze the composer lock chip. Session-panel creates pass this before
   * ``data_scope`` exists. Work home passes it for a restricted project so
   * fullscreen / reopen matches in-session (Figma docked chat).
   */
  lockComposerContext?: boolean;
  /** Session panel: scope the composer's session pickers to the host patient. */
  composerPatientScope?: WorkSessionComposerProps['patientScope'];
  composerSlashSectionOrder?: WorkSessionComposerProps['slashSectionOrder'];
  /** In-session panel only. Adds the shortcuts flyout to the + menu. */
  composerSessionShortcutFlyout?: boolean;
  composerAutoFocus?: boolean;
  onComposerFocusChange?: (focused: boolean) => void;
  onComposerDraftEmptyChange?: (empty: boolean) => void;
  /** Optional embedder-owned draft scope. Omit for the existing project-key behavior. */
  draftStorageScope?: string;
  /** A draft the host holds across this chat's lifetime; see the composer session. */
  hostDraft?: ProjectsComposerSession['hostDraft'];
  composerExpanded?: boolean;
  onComposerExpandedChange?: (expanded: boolean) => void;
  /** Host-owned chrome above the composer. Credit banners fill this when omitted. */
  composerDockLeading?: ReactNode;
  /** Suppress backend-generated reply chips while an embedding flow owns the
   * next step (for example, a routine setup interview). */
  hideFollowUpQuestions?: boolean;
  centerProvisionalContent?: boolean;
  /** Session inspect: `readOnlyFooter` replaces the composer. Sharing uses transport.readOnly instead. */
  readOnly?: boolean;
  readOnlyFooter?: ReactNode;
  /**
   * File-drop takeover. Unset keeps the composer's own drop card.
   * `'window'` fills `fileDropContent` — the shell content column, so the ribbon stays outside the drag.
   * `'panel'` fills `fileDropBoundaryRef` — the docked chat column, under the header.
   */
  fileDropScope?: 'window' | 'panel';
  /** Shell content column for `fileDropScope='window'`. The route passes it. */
  fileDropContent?: HTMLElement | null;
  /** Boundary element for `fileDropScope='panel'` (the docked chat column, under the header). */
  fileDropBoundaryRef?: RefObject<HTMLElement>;
  /** F4 wires fork; F2 shows the banner CTA only when this is passed. */
  onContinueSharedProject?: () => void;
}

/** Optimistic first-turn props shared by provisional and bound modes. */
interface ProjectChatOptimisticProps {
  /** Opening bubble echoed until harness `items` are non-empty. */
  provisionalFirstTurnText?: string | null;
  /** True while a create (or similar) round-trip is in flight — drives composer busy. */
  provisionalIsSubmitting?: boolean;
}

interface ProjectChatBoundProps
  extends ProjectChatSharedProps, ProjectChatOptimisticProps {
  projectId: string;
  onProvisionalSubmit?: never;
}

/**
 * `bound` — this chat now has a project and finishes the turn itself; `consumed`
 * — the host took the turn elsewhere; `refused` — the draft returns silently.
 */
export type ProvisionalSubmitOutcome = 'bound' | 'consumed' | 'refused';

/** Provisional: no project yet, and this instance stays mounted through the create. */
interface ProjectChatProvisionalProps
  extends ProjectChatSharedProps, ProjectChatOptimisticProps {
  projectId: undefined;
  /** The host creates the project and binds this chat to it, then this chat
   *  finishes the turn. */
  onProvisionalSubmit: (args: SubmitProjectTurnArgs) => Promise<ProvisionalSubmitOutcome>;
}

export type ProjectChatProps = ProjectChatBoundProps | ProjectChatProvisionalProps;

/** The credit allowance chrome the app resolves for this chat. */
export interface ProjectChatCreditChrome {
  composerDockLeading: ReactNode;
  transcriptTrailing: ReactNode;
  hideFollowUpQuestions: boolean;
  sendDisabled: boolean;
}

/** What the chat reads off the patient profile behind a locked composer. */
export type ProjectChatPatientProfile = NonNullable<
  Parameters<typeof patientProfileDisplayName>[0]
>;

/** The Work experience (UMUX-Lite) nudge, as the chat drives it. */
export interface ProjectChatFeedbackNudgeProps {
  easeRating: CSATRating | null;
  usefulnessRating: CSATRating | null;
  detail: string;
  isRatingSubmitted: boolean;
  onEaseRatingSubmit: (rating: CSATRating) => void;
  onUsefulnessRatingSubmit: (rating: CSATRating) => void;
  onDetailChange: (detail: string) => void;
  onSubmit: () => void;
  onDismiss: () => void;
  isSubmitDisabled: boolean;
  labels: {
    questionEase: string;
    questionUsefulness: string;
    titlePhase2: string;
    placeholderImprove: string;
    submit: string;
  };
  deferCommentUntilUsefulness?: boolean;
}

type ProjectBackgroundRegistrationArgs = Parameters<
  typeof useProjectBackgroundRegistration
>[0];
type ProjectDesktopDelegationArgs = Omit<
  Parameters<typeof useProjectDesktopDelegationCore>[0],
  'ports'
>;
type ProjectChatTimelineSlotProps = Omit<
  ComponentProps<typeof ProjectConversationTimelineView>,
  'itemRendererHooks' | 'insetDesign'
>;

/**
 * What the app supplies, as one module-level object so every member keeps one
 * identity. The chat calls each hook where it always called it, in the same
 * order; the plain members are read where the chat always read them.
 */
export interface ProjectChatHost {
  /** The routine run this project belongs to, if any. Only its id is read. */
  useRoutineForProject: (projectId: string) => {
    workflow?: { workflow_id: string } | null;
  };
  /** The credit allowance banners and gates for this chat. */
  useCreditAllowanceChrome: (options: { inThread: boolean }) => ProjectChatCreditChrome;
  /** The project's harness transport, bound to the app. */
  useProjectTransport: (
    projectId: string | undefined,
    options?: {
      workspace?: WorkspaceSnapshotV1;
      desktopTargetRequirement?: 'all-turns' | 'computer-use-only';
    }
  ) => ProjectTransport;
  /** The desktop permission gate a computer-use step asks before it acts. */
  useDesktopPermissionGate: () => {
    ensurePermissions: (mode: 'computer_use') => Promise<boolean>;
  };
  useIsProjectsContextualLoaderPhrasesEnabled: () => boolean;
  useIsWorkLetterArtifactEnabled: () => boolean;
  /** The workspace's letter-flow entitlement. */
  useLetterFlowEntitlement: () => { isEnabled: boolean; isPending: boolean };
  /** The patient profile behind a locked composer, for its name. */
  useQueryPatientProfileById: (
    patientProfileId: string | undefined,
    options: { enabled: boolean; isAuthenticated: boolean }
  ) => { data?: ProjectChatPatientProfile | null };
  /** Whether the copilot escalation for this project has a patient bound. */
  useRequiresRedactions: (projectId: string) => boolean;
  /** The desktop delegation, bound to the app's executor. */
  useProjectDesktopDelegation: (
    args: ProjectDesktopDelegationArgs
  ) => ReturnType<typeof useProjectDesktopDelegationCore>;
  /** The items with a refused first turn's ghost bubble merged in. */
  useCreditAllowanceGhostItems: (items: ChatItem[], projectId: string) => ChatItem[];
  useIsWorkSessionArtifactV2Enabled: () => boolean;
  /** The turn-end artifact auto-open, bound to the app's cache reactions. */
  useProjectArtifactAutoOpen: (
    args: Parameters<typeof useProjectArtifactAutoOpenCore>[0]
  ) => void;
  /** Feeds note-push tool results into the app's candidate store. */
  useIngestComputerUseNotePushCandidates: (
    args: Parameters<typeof useIngestComputerUseNotePushCandidatesCore>[0]
  ) => void;
  /** Publishes the note-push apply bridge, bound to the app. */
  usePublishNotePushApply: (
    params: Parameters<typeof usePublishNotePushApplyCore>[0]
  ) => void;
  /** The created-artifact resolvers; the chat adds the letter verdict. */
  createdArtifactPorts: Omit<CreatedChatArtifactPorts, 'isLetterArtifactEnabled'>;
  registerProjectBackground: ProjectBackgroundRegistrationArgs['registerProjectBackground'];
  unregisterProjectBackground: ProjectBackgroundRegistrationArgs['unregisterProjectBackground'];
  /** Session's own list invalidation, which the auto-open reaches on a session write. */
  invalidateSessionsList: () => void;
  trackProjectChatErrorOnce: TrackProjectChatErrorOnce;
  trackWorkExperienceFeedbackActioned: TrackWorkExperienceFeedbackActioned;
  trackWorkRpaDesktopRequiredShown: TrackWorkRpaDesktopRequiredShown;
  /** Emits the "Continue on mobile" card's lifecycle (AGE-2889). */
  trackMobileHandoffCardActioned: (
    action: ContinueOnMobileCardAction,
    props: { project_id?: string; agent_run_id?: string }
  ) => void;
  /** Returns a callback that opens the get-mobile QR modal, which the app owns. */
  useOpenGetMobileApp: () => () => void;
  /** Whether this deployment offers product feedback, read where the nudge asks. */
  isProductFeedbackAvailable: () => boolean;
  /** Remembers a credit-refused first turn so its ghost shows. */
  rememberCreditAllowanceRefusal: Parameters<
    typeof useProjectSeededFirstTurn
  >[0]['onCreditAllowanceRefused'];
  /** Whether the desktop bridge is available, read on each render. */
  isDesktopAvailable: () => boolean;
  /** A development build: warns when no Sources control can show. */
  isDevelopmentBuild: boolean;
  /** The resident drawer's selector verdict, cloud machine and live screen. */
  residentDrawer: YourResidentDrawerHost;
  /** The composer, driven through the handle it forwards to its ref. */
  ProjectChatComposer: ComponentType<
    WorkSessionComposerProps & { ref?: Ref<ProjectsComposerHandle> }
  >;
  ProjectConversationTimeline: ComponentType<ProjectChatTimelineSlotProps>;
  ProjectReplyMessageActions: ComponentType<ProjectReplyMessageActionsProps>;
  ProjectMessageActions: ComponentType<ProjectMessageActionsProps>;
  /** An assistant turn's prose. */
  AssistantText: ComponentType<{ text: string; streaming: boolean }>;
  ExperienceFeedbackNudge: ComponentType<ProjectChatFeedbackNudgeProps>;
}

const OpeningAssistantMessageWithFeedback = ({
  message,
  projectId,
  AssistantText,
  MessageActions,
}: {
  message: string;
  projectId: string;
  AssistantText: ProjectChatHost['AssistantText'];
  MessageActions: ProjectChatHost['ProjectMessageActions'];
}) => {
  const submitFeedback = useSubmitProjectFeedback(projectId);
  const handleSubmitFeedback = useCallback(
    (rating: ProjectFeedbackRating, comment?: string) => {
      if (!projectId) {
        return Promise.reject(
          new Error('Cannot submit opening-message feedback before a project exists')
        );
      }
      return submitFeedback.mutateAsync({
        rating,
        // The greeting is synthetic rather than a persisted transcript reply;
        // the backend contract uses null for project-level feedback.
        message_id: null,
        user_feedback: comment ?? null,
      });
    },
    [projectId, submitFeedback]
  );

  return (
    <div className="mb-5">
      <AssistantText text={message} streaming={false} />
      <MessageActions
        className="mt-2"
        projectId={projectId || undefined}
        copyableText={message}
        onSubmitFeedback={handleSubmitFeedback}
        feedbackDisabled={!projectId}
      />
    </div>
  );
};

/**
 * Chat surface for a project, modeled on the Evidence composer.
 *
 * The transcript is the agent-harness timeline: the host's transport
 * reads the project's durable run via the harness stream (snapshot +
 * live SSE) into a reconciled ``ChatItem[]`` and renders it through
 * `<AgentTimeline renderItem>` with Evidence-styled project blocks. The
 * write path stays on the Projects client (attachments aren't supported
 * on the harness turn) — ``transport.startTurn`` hands the harness an
 * optimistic user bubble first, then uploads any staged files and POSTs
 * the turn, withdrawing the bubble if that round-trip fails.
 *
 * What the app owns arrives in one {@link ProjectChatHost}; the embedder mounts
 * this inside the desktop-target view selection it provides.
 */
export const ProjectChatContent = ({
  projectId,
  projectTitle,
  artifactSink,
  workspace,
  provisionalFirstTurnText,
  provisionalIsSubmitting = false,
  onProvisionalSubmit,
  onTurnInFlightChange,
  onTurnControlsChange,
  emptyState,
  prefilledPrompt,
  analyticsSurface,
  analyticsClinicalSessionId,
  desktopTargetRequirement,
  onHasQueuedTurnsChange,
  timelineLeadingContent,
  transcriptTextScale = 'default',
  composerPrefill,
  openingAssistantMessage,
  openingAssistantMessageFeedback,
  timelineTopFadeClassName,
  timelineFadeColorClassName,
  composerBackdrop,
  composerToolbarLeading,
  composerSpeedMenu,
  composerMode,
  onComposerModeChange,
  composerPlaceholderPreview,
  composerSessionAppearance,
  composerLayout = 'full',
  toolbarSourcesSlot,
  composerEvidenceSourcesSlot,
  lockComposerContext = false,
  composerPatientScope,
  composerSlashSectionOrder,
  composerSessionShortcutFlyout = false,
  composerAutoFocus,
  onComposerFocusChange,
  onComposerDraftEmptyChange,
  draftStorageScope,
  hostDraft,
  composerExpanded = false,
  onComposerExpandedChange,
  composerDockLeading,
  hideFollowUpQuestions = false,
  centerProvisionalContent = false,
  readOnly = false,
  readOnlyFooter,
  fileDropScope,
  fileDropContent,
  fileDropBoundaryRef,
  onContinueSharedProject,
  host,
}: ProjectChatProps & { host: ProjectChatHost }) => {
  const {
    createdArtifactPorts: hostCreatedArtifactPorts,
    registerProjectBackground,
    unregisterProjectBackground,
    invalidateSessionsList,
    trackProjectChatErrorOnce,
    trackWorkExperienceFeedbackActioned,
    trackWorkRpaDesktopRequiredShown,
    rememberCreditAllowanceRefusal,
    isDevelopmentBuild,
    residentDrawer,
    ProjectChatComposer,
    ProjectConversationTimeline,
    ProjectReplyMessageActions,
    ProjectMessageActions,
    AssistantText,
    ExperienceFeedbackNudge,
  } = host;
  // Provisional: mounted with no project yet (the in-session Work panel while a
  // create is in flight, or before the first send). All project-scoped hooks
  // below take `pid` (empty string ⇒ disabled query / no `agent_run_id`), so
  // they idle until a real id lands — no remount when it does.
  const provisional = !projectId;
  const pid = projectId ?? '';
  const { workflow: runRoutine } = host.useRoutineForProject(pid);
  const inheritedSourcesSlot = useWorkComposerSourcesSlot();
  const resolvedToolbarSourcesSlot = toolbarSourcesSlot ?? inheritedSourcesSlot;
  useEffect(() => {
    if (
      isDevelopmentBuild &&
      composerLayout !== 'condensed' &&
      !resolvedToolbarSourcesSlot
    ) {
      console.error(
        'ProjectChat layout is not condensed and has no Sources toolbar slot. Sources is not shown in the + menu.'
      );
    }
  }, [composerLayout, isDevelopmentBuild, resolvedToolbarSourcesSlot]);
  const credit = host.useCreditAllowanceChrome({
    inThread: Boolean(projectId),
  });
  const transport = host.useProjectTransport(
    projectId,
    workspace || desktopTargetRequirement
      ? {
          ...(workspace ? { workspace } : {}),
          ...(desktopTargetRequirement ? { desktopTargetRequirement } : {}),
        }
      : undefined
  );
  const contextLocked =
    lockComposerContext || isRestrictedProjectDataScope(transport.dataScope);
  const {
    recordingEligible,
    readOnly: shareReadOnly,
    ownerDisplayName,
    streamStatus,
  } = transport;
  const inspectReadOnly = readOnly;
  const lockMutations = inspectReadOnly || shareReadOnly;
  const isGrantRevoked = shareReadOnly && streamStatus === 'error';
  const composerRef = useRef<ProjectsComposerHandle>(null);
  const isMobile = useIsMobile();
  const intl = useIntl();
  const [pendingComposerPrompt, setPendingComposerPrompt] = useAtom(
    pendingComposerPromptAtom
  );
  const [prefilledComposerPrompt, setPrefilledComposerPrompt] = useState<
    string | undefined
  >();

  // A read-only host leaves the hand-off for the editable chat it navigates to.
  const composerHandOff =
    !readOnly && pid && pendingComposerPrompt?.projectId === pid
      ? pendingComposerPrompt
      : null;

  useLayoutEffect(() => {
    if (!composerHandOff || composerHandOff.submit) return;
    setPrefilledComposerPrompt(composerHandOff.prompt);
    setPendingComposerPrompt(null);
  }, [composerHandOff, setPendingComposerPrompt]);

  // The turn owner starts in a passive effect; a send at layout timing is dropped.
  useEffect(() => {
    if (!composerHandOff?.submit) return;
    composerRef.current?.submitPrompt(composerHandOff.prompt, { preserveDraft: true });
    setPendingComposerPrompt(null);
  }, [composerHandOff, setPendingComposerPrompt]);

  useLayoutEffect(() => {
    if (composerPrefill === undefined) return;
    setPrefilledComposerPrompt(composerPrefill);
  }, [composerPrefill]);
  const { ensurePermissions } = host.useDesktopPermissionGate();
  const {
    items,
    agentRunId,
    status,
    isLoading,
    turnInFlight,
    isSubmitting,
    cancelling,
    loaderPhrases,
    discardedTurnNonce,
    discardedTurn,
  } = transport;
  const isContextualLoaderPhrasesEnabled =
    host.useIsProjectsContextualLoaderPhrasesEnabled();
  const contextualLoaderPhrases = isContextualLoaderPhrasesEnabled
    ? loaderPhrases
    : undefined;
  const isWorkLetterArtifactEnabled = host.useIsWorkLetterArtifactEnabled();
  const { isEnabled: isLetterFlowEntitled, isPending: isLetterArtifactAccessPending } =
    host.useLetterFlowEntitlement();
  const isLetterArtifactEnabled = isWorkLetterArtifactEnabled && isLetterFlowEntitled;
  const createdArtifactPorts = useMemo(
    () => ({ ...hostCreatedArtifactPorts, isLetterArtifactEnabled }),
    [hostCreatedArtifactPorts, isLetterArtifactEnabled]
  );
  usePublishCreatedChatArtifacts(projectId, items, createdArtifactPorts);
  const userArtifactRevision = useAtomValue(artifactUserRevisionAtom);
  const openArtifact = useAtomValue(openArtifactAtom);

  // Run-level failure analytics are guarded per mounted run.
  const failedErrorFiredForRunRef = useRef<string | null>(null);

  useEffect(() => {
    if (status !== 'failed') {
      failedErrorFiredForRunRef.current = null;
      return;
    }
    const runKey = agentRunId ?? `failed:${pid ?? ''}`;
    if (failedErrorFiredForRunRef.current === runKey) return;
    failedErrorFiredForRunRef.current = runKey;
    trackProjectChatErrorOnce({
      project_id: pid || undefined,
      agent_run_id: agentRunId,
      surface: 'project_chat',
      error_type: 'run_failed',
    });
  }, [status, pid, agentRunId, trackProjectChatErrorOnce]);

  // Linked context (single-slot, jotai). Hydrated by the detail-page
  // URL-param effect when arriving from a Work-home tile click, or
  // by the LinkContextButton picker on the home composer (state
  // survives navigation). We render the chip above the textarea and
  // serialise the preamble into the first send, then clear so the
  // chip doesn't persist into the next prompt.
  const linkedItems = useAtomValue(composerLinkedItemsAtom);
  const isAuthenticated = useIsAuthenticated();
  const patientProfileId = lockedComposerPatientFetchId({
    enabled: contextLocked,
    linkedItems,
    dataScope: transport.dataScope,
  });
  const { data: fetchedPatientProfile } = host.useQueryPatientProfileById(
    patientProfileId,
    {
      enabled: Boolean(patientProfileId),
      isAuthenticated,
    }
  );
  const contextLock = resolveContextLock({
    linkedItems,
    dataScope: transport.dataScope,
    fetchedPatientName: patientProfileDisplayName(fetchedPatientProfile),
    restricted: contextLocked,
  });
  const clearLinkedItems = useSetAtom(clearComposerLinkedItemsAtom);
  // Reveal setter — backs the in-transcript "Open" affordances (direct
  // user intent), so it pops the mobile sheet. Default only: an embedder
  // (e.g. the in-session Work panel) can redirect this via `artifactSink`.
  const setOpenArtifact = useSetAtom(setOpenArtifactAtom);
  // Silent setter — the agent auto-open path populates the canvas but
  // must not yank a sheet over the transcript on mobile. Default only —
  // see `artifactSink`.
  const setOpenArtifactSilent = useSetAtom(setOpenArtifactSilentAtom);
  const onOpenArtifact = artifactSink?.onOpenArtifact ?? setOpenArtifact;
  const onOpenArtifactSilent =
    artifactSink?.onOpenArtifactSilent ?? setOpenArtifactSilent;

  // Mirror the live turn state up to the embedder; reset when this chat
  // unmounts so it can't leave a stale "in flight" behind.
  useEffect(() => {
    if (!onTurnInFlightChange) return undefined;
    onTurnInFlightChange(turnInFlight);
    return () => onTurnInFlightChange(false);
  }, [turnInFlight, onTurnInFlightChange]);

  const runFailed = status === 'failed';
  const runClosed = status === 'closed';

  const { activeClarificationApproval, hasPendingApproval, pendingApprovals } =
    useProjectPendingApprovals({
      agentRunId,
      items,
      pendingApprovalToolCallIds: transport.pendingApprovalToolCallIds,
    });

  const steerPendingApprovals = useSteerPendingApprovals({
    agentRunId,
    approvals: pendingApprovals,
    presentedApprovalId: activeClarificationApproval?.approvalId,
  });

  const isMobileHandoffCtaEnabled = useIsWorkMobileHandoffCtaEnabled();
  const openGetMobileApp = host.useOpenGetMobileApp();
  const showContinueOnMobileCard =
    isMobileHandoffCtaEnabled && hasPendingApproval && !lockMutations && !isMobile;
  const { trackMobileHandoffCardActioned } = host;
  const trackMobileHandoffCard = useCallback(
    (action: ContinueOnMobileCardAction) =>
      trackMobileHandoffCardActioned(action, {
        project_id: pid,
        agent_run_id: agentRunId,
      }),
    [agentRunId, pid, trackMobileHandoffCardActioned]
  );

  // A note-push project has a patient bound; the registry persists that fact
  // (never the name). Selected down to one boolean so unrelated context writes
  // don't re-render the (heavy) subscriber tree.
  const requiresRedactions = host.useRequiresRedactions(pid);
  useProjectBackgroundRegistration({
    projectId: pid,
    projectTitle,
    agentRunId,
    runFailed,
    runClosed,
    desktopTargetEnabled: transport.desktopTargetEnabled,
    enabled: !lockMutations,
    requiresRedactions,
    registerProjectBackground,
    unregisterProjectBackground,
  });

  const { inFlightDesktopCall, liveDelegateStep, stopTurn, wrappingUp } =
    host.useProjectDesktopDelegation({
      items,
      turnInFlight,
      cancelParentTurn: lockMutations ? () => undefined : transport.cancelTurn,
    });
  const viewerLiveDelegateStep = lockMutations ? undefined : liveDelegateStep;

  const {
    displayItems: optimisticItems,
    showPreview: showSeededFirstTurnPreview,
    clearPreview: clearRejectedSeededFirstTurnPreview,
    shouldAnimateUserMessage,
  } = useOptimisticFirstTurn({
    projectId: pid,
    items,
    runFailed,
    runClosed,
    // Echo the embedder-owned opening bubble (in-session panel / provisional
    // chat) while the harness timeline is empty. The hook prefers this
    // declarative text over the seeded turn's `showPreview`, so the embedder
    // keeps owning the bubble across the provisional -> bound flip; the routed
    // page never passes it and rides `showPreview` + the cross-mount store.
    previewText: provisionalFirstTurnText ?? null,
    discardedTurnNonce,
  });
  const displayItems = host.useCreditAllowanceGhostItems(optimisticItems, pid);
  const boundStartTurnRef = useRef<ProjectTransport['startTurn'] | null>(null);
  const provisionalRef = useLatestRef(provisional);
  // Hands the opening turn to the host, then finishes it on the transport the
  // host's bind just produced — one operation the turn-command owner holds on.
  const provisionalStartTurn = useCallback<ProjectTransport['startTurn']>(
    (command) => {
      const turnId = command.turnId ?? mintTurnId();
      const turn = {
        ...command,
        turnId,
        approvalMode: command.approvalMode ?? transport.approvalMode,
      };
      if (!onProvisionalSubmit) {
        return createRefusedTurnOperation(
          turnId,
          new Error('Provisional submit handler missing while projectId is undefined')
        );
      }

      const deferred = createDeferredTurnOperation(turnId);
      void (async () => {
        try {
          const outcome = await onProvisionalSubmit(turn);
          if (outcome === 'consumed') {
            // The host seeded this turn elsewhere. Reject only as an internal
            // owner signal: release its hold without restoring or toasting.
            deferred.abandon(
              'handed_off',
              new ProvisionalSubmitRejectedError({ restoreDraft: false })
            );
            return;
          }
          const boundStart = boundStartTurnRef.current;
          // Still provisional after a `bound`: adopting here would recurse.
          if (outcome === 'refused' || provisionalRef.current || !boundStart) {
            deferred.abandon('submit_rejected', new ProvisionalSubmitRejectedError());
            return;
          }
          deferred.adopt(boundStart(turn));
        } catch (error) {
          deferred.abandon('submit_rejected', error);
        }
      })();

      return deferred.operation;
    },
    [onProvisionalSubmit, provisionalRef, transport.approvalMode]
  );
  const rawStartTurn = provisional ? provisionalStartTurn : transport.startTurn;
  // A create the host still drives keeps the composer busy past the bind, so the
  // queue cannot drain before the opening turn's POST.
  const composerIsSubmitting = provisionalIsSubmitting || (!provisional && isSubmitting);

  // Mirror the stop control up alongside the turn state, so a host header can
  // offer its own Stop — but only while there is actually a live turn to stop.
  // `turnInFlight` goes true on the optimistic submit, before the backend has
  // anything to cancel, so the send window is excluded as the composer's own
  // Stop excludes it. Kept out of the cleanup on dep changes — nulling and
  // re-setting between commits would blink the host's button.
  useEffect(() => {
    onTurnControlsChange?.(
      !lockMutations && turnInFlight && !composerIsSubmitting
        ? { stopTurn, cancelling }
        : null
    );
  }, [
    lockMutations,
    turnInFlight,
    composerIsSubmitting,
    stopTurn,
    cancelling,
    onTurnControlsChange,
  ]);
  useEffect(() => () => onTurnControlsChange?.(null), [onTurnControlsChange]);

  // At turn end, auto-open the turn's one artifact (or refresh it in place); several open nothing.
  const setArtifactFieldUpdate = useSetAtom(artifactFieldUpdateAtom);
  const queryClient = useQueryClient();
  const isWorkSessionArtifactV2Enabled = host.useIsWorkSessionArtifactV2Enabled();
  host.useProjectArtifactAutoOpen({
    items,
    isLoading: isLoading || lockMutations,
    isLetterArtifactEnabled,
    isLetterArtifactAccessPending,
    suppressGenericSessionAutoOpen: isWorkSessionArtifactV2Enabled,
    turnInFlight,
    openArtifact,
    userArtifactRevision,
    queryClient,
    setArtifactFieldUpdate,
    setOpenArtifact: onOpenArtifactSilent,
    onSessionsChanged: invalidateSessionsList,
  });

  // Feed note-push tool results into the ephemeral candidate store the
  // template-builder mapping chips read (write-side; read-side lives in the
  // panel). Headless, resets per project.
  host.useIngestComputerUseNotePushCandidates({ items, projectId: pid });

  // Work experience feedback (UMUX-Lite) — after 2 completed assistant replies
  const completedQueryCount = useMemo(
    () =>
      items.filter((item) => item.kind === 'assistant_text' && !item.streaming).length,
    [items]
  );
  const {
    shouldShowNudge: shouldShowWorkFeedbackNudge,
    hideNudge: handleWorkFeedbackDismiss,
  } = useWorkExperienceFeedbackTrigger({
    queryCount: completedQueryCount,
    isStreaming: turnInFlight,
    projectId: provisional ? null : pid,
    trackWorkExperienceFeedbackActioned,
    isProductFeedbackAvailable: host.isProductFeedbackAvailable(),
  });
  const {
    easeRating: workFeedbackEaseRating,
    usefulnessRating: workFeedbackUsefulnessRating,
    detail: workFeedbackDetail,
    isRatingSubmitted: isWorkFeedbackRatingSubmitted,
    submitEaseRating: handleWorkFeedbackEaseRatingSubmit,
    submitUsefulnessRating: handleWorkFeedbackUsefulnessRatingSubmit,
    setDetail: setWorkFeedbackDetail,
    submitFeedback: handleWorkFeedbackSubmit,
    isSubmitDisabled: isWorkFeedbackSubmitDisabled,
  } = useWorkExperienceFeedbackSubmission({
    queryCount: completedQueryCount,
    projectId: provisional ? undefined : pid,
    trackWorkExperienceFeedbackActioned,
  });
  const workFeedbackLabels = useMemo(
    () => ({
      questionEase: intl.formatMessage({
        defaultMessage: 'How did Heidi do?',
        id: 'AzwK1xmnOV',
        description: 'Work experience feedback nudge: overall experience question',
      }),
      questionUsefulness: intl.formatMessage({
        defaultMessage: 'How useful was Heidi for you?',
        id: 'M6dtCOk513',
        description: 'Work experience feedback nudge: usefulness question',
      }),
      titlePhase2: intl.formatMessage({
        defaultMessage: 'Your feedback helps us improve',
        id: 'IIhjareCha',
        description: 'Work experience feedback nudge: phase 2 title',
      }),
      placeholderImprove: intl.formatMessage({
        defaultMessage: 'How could we improve?',
        id: 'k3sCqt9/E0',
        description: 'Work experience feedback nudge: free-text placeholder',
      }),
      submit: intl.formatMessage({
        defaultMessage: 'Submit',
        id: '56LPU/6itG',
        description: 'Work experience feedback nudge: submit button',
      }),
    }),
    [intl]
  );

  const isStartingSeededTurn = getIsStartingSeededTurn({
    projectId: pid,
    itemsLength: items.length,
    runFailed,
    runClosed,
  });

  const [hasQueuedTurns, setHasQueuedTurns] = useState(false);
  const handleHasQueuedTurnsChange = useCallback(
    (queued: boolean) => {
      setHasQueuedTurns(queued);
      onHasQueuedTurnsChange?.(queued);
    },
    [onHasQueuedTurnsChange]
  );

  const conversationTimeline = useProjectConversationTimeline({
    items: displayItems,
    agentRunId,
    runFailed,
    runClosed,
    turnInFlight,
    cancelling,
    isSubmitting: composerIsSubmitting,
    isLoading,
    isStartingSeededTurn,
    thinkingPhrases: contextualLoaderPhrases,
    hasPendingApproval,
    hasQueuedTurns,
    inFlightDesktopCall: !!inFlightDesktopCall,
    startTurn: lockMutations ? undefined : rawStartTurn,
    shouldAnimateUserMessage,
  });
  // Always supplied above; optional only for the RPA overlay composition.
  const startTurnWithScrollSnap = conversationTimeline.startTurn!;
  boundStartTurnRef.current =
    provisional || lockMutations ? null : startTurnWithScrollSnap;
  const scrollTranscript = useCallback(() => {
    conversationTimeline.scrollToBottom?.();
  }, [conversationTimeline]);
  const { isEmpty, isInitialLoading, latestTurnHasReply } = conversationTimeline;

  const enqueueViaOwner = useCallback((command: SubmitProjectTurnArgs): TurnOperation => {
    const handle = composerRef.current;
    if (!handle) {
      return createRefusedTurnOperation(
        command.turnId ?? mintTurnId(),
        new Error('Work chat owner is not mounted')
      );
    }
    return handle.enqueueTurn(command);
  }, []);
  const enqueueSeedViaOwner = useCallback(
    (command: SubmitProjectTurnArgs): TurnOperation => {
      const handle = composerRef.current;
      if (!handle) {
        return createRefusedTurnOperation(
          command.turnId ?? mintTurnId(),
          new Error('Work chat owner is not mounted')
        );
      }
      return handle.enqueueTurnNow(command);
    },
    []
  );

  usePublishProjectChatSubmit(enqueueViaOwner, !lockMutations);
  // Publish the note-push apply bridge so the verification panel can route its
  // confirmed mapping into a real turn (the structured handoff).
  host.usePublishNotePushApply({
    enqueueTurn: enqueueViaOwner,
    projectId: lockMutations ? '' : pid,
    isSubmitting,
    turnInFlight,
  });

  const onSeededFirstTurnRejected = useCallback(
    (restoreDraftText?: string) => {
      clearRejectedSeededFirstTurnPreview();
      if (restoreDraftText) composerRef.current?.seedComposer(restoreDraftText);
    },
    [clearRejectedSeededFirstTurnPreview]
  );

  useProjectSeededFirstTurn({
    projectId: lockMutations ? '' : pid,
    agentRunId,
    itemsLength: items.length,
    runFailed,
    runClosed,
    enqueueTurn: enqueueSeedViaOwner,
    onSubmitStart: showSeededFirstTurnPreview,
    onSubmitRejected: onSeededFirstTurnRejected,
    onCreditAllowanceRefused: rememberCreditAllowanceRefusal,
  });

  // The queue and draft go with this chat; a host guard must not outlive it.
  useEffect(() => () => onHasQueuedTurnsChange?.(false), [onHasQueuedTurnsChange]);
  useEffect(() => () => onComposerDraftEmptyChange?.(true), [onComposerDraftEmptyChange]);

  const latestOnDemandDesktopRequired = useMemo(
    () => findLatestOnDemandDesktopRequired(displayItems),
    [displayItems]
  );
  const [dismissedComputerUseNudgeId, setDismissedComputerUseNudgeId] = useState<
    string | null
  >(null);
  const dismissComputerUseNudge = useCallback(() => {
    if (latestOnDemandDesktopRequired) {
      setDismissedComputerUseNudgeId(latestOnDemandDesktopRequired.id);
    }
  }, [latestOnDemandDesktopRequired]);
  const computerUseBannerKind = resolveComputerUseComposerBanner({
    missId: latestOnDemandDesktopRequired?.id ?? null,
    dismissedId: dismissedComputerUseNudgeId,
    isDesktopAvailable: host.isDesktopAvailable(),
  });
  const retryComputerUseFromDesktop = useCallback(() => {
    composerRef.current?.submitPrompt(
      buildComputerUseRetryPrompt(latestOnDemandDesktopRequired?.task)
    );
    dismissComputerUseNudge();
  }, [dismissComputerUseNudge, latestOnDemandDesktopRequired?.task]);

  const handleTranscriptPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      handleTranscriptEngagement(event, () => {
        composerRef.current?.notifyTranscriptEngage();
      });
    },
    []
  );

  const latestTimelineItem = conversationTimeline.displayItems.at(-1);
  const replyChromeGateArgs = {
    isInitialLoading,
    isEmpty,
    turnInFlight,
    isSubmitting,
    runFailed,
    runClosed,
    hasPendingApproval,
    latestTurnHasReply,
    latestTurnIsFailed: latestTimelineItem?.kind === 'turn_failed',
    latestTurnFailureReason:
      latestTimelineItem?.kind === 'turn_failed' ? latestTimelineItem.reason : null,
    hasQueuedTurns,
    hideFollowUpQuestions: credit.hideFollowUpQuestions || hideFollowUpQuestions,
  };
  const showReplyMessageActions =
    !lockMutations && shouldShowProjectReplyFeedback(replyChromeGateArgs);
  const showEarlierReplyActions =
    useIsWorkEarlierResponseActionsEnabled() && !lockMutations;
  // Copy needs no live run, so a closed run's final reply keeps a copy-only row.
  const showReplyCopyOnly =
    showEarlierReplyActions &&
    runClosed &&
    latestTurnHasReply &&
    shouldShowProjectReplyFeedback({ ...replyChromeGateArgs, runClosed: false });
  const showLatestReplyActions = showReplyMessageActions || showReplyCopyOnly;
  const showFollowUpQuestions =
    !lockMutations && shouldShowProjectFollowUpQuestions(replyChromeGateArgs);
  const latestInlineReplyToolCallId = showLatestReplyActions
    ? findTrailingEvidenceChildToolCallId(conversationTimeline.displayItems)
    : null;
  const givenReplyRatings = useAtomValue(givenReplyRatingsAtom);
  const latestReplyMessageId = String(items.length - 1);
  const transcriptIndexById = useMemo(
    () => new Map(items.map((item, index) => [item.id, index])),
    [items]
  );
  const inlineReplyActions = useMemo(() => {
    const actions = new Map<string, (copyableText: string | null) => ReactNode>();
    if (showEarlierReplyActions) {
      for (const turn of findEarlierEvidenceReplyTurns(
        conversationTimeline.displayItems
      )) {
        const messageId = resolveCompletedTurnMessageId(
          transcriptIndexById,
          turn.items,
          turn.nextTurnFirstItem
        );
        if (messageId === null) continue;
        actions.set(turn.toolCallId, (copyableText) => (
          <ProjectReplyMessageActions
            projectId={pid}
            displayItems={turn.items}
            copyableText={copyableText}
            messageId={messageId}
            givenRatings={givenReplyRatings}
            visibility={EARLIER_RESPONSE_ACTIONS_VISIBILITY}
          />
        ));
      }
    }
    if (latestInlineReplyToolCallId) {
      actions.set(latestInlineReplyToolCallId, (copyableText) => (
        <ProjectReplyMessageActions
          projectId={pid}
          displayItems={conversationTimeline.displayItems}
          copyableText={copyableText}
          messageId={latestReplyMessageId}
          givenRatings={givenReplyRatings}
          hideFeedback={showReplyCopyOnly}
        />
      ));
    }
    return actions.size > 0 ? actions : undefined;
  }, [
    showEarlierReplyActions,
    conversationTimeline.displayItems,
    transcriptIndexById,
    latestInlineReplyToolCallId,
    pid,
    latestReplyMessageId,
    givenReplyRatings,
    showReplyCopyOnly,
    ProjectReplyMessageActions,
  ]);
  const followUpReplyId = latestTimelineItem
    ? `${agentRunId ?? pid}:${latestTimelineItem.id}`
    : '';

  const handleFollowUpSelect = useCallback((question: string) => {
    composerRef.current?.submitPrompt(question);
  }, []);

  const handleChatFilesAccepted = useCallback(
    (files: File[]) => {
      if (runClosed || lockMutations) return;
      composerRef.current?.stageFiles(files);
    },
    [runClosed, lockMutations]
  );

  const handleChatFilesRejected = useCallback(
    (rejections: readonly FileUploadRejection[]) => {
      const { error } = mergePickedFiles([], [], { dropRejections: rejections });
      if (error) toast({ ...error, tone: 'destructive' });
    },
    []
  );

  const leadingContent = useMemo(() => {
    if (!openingAssistantMessage && !timelineLeadingContent) return undefined;
    return (
      <>
        {openingAssistantMessage ? (
          openingAssistantMessageFeedback ? (
            <OpeningAssistantMessageWithFeedback
              message={openingAssistantMessage}
              projectId={pid}
              AssistantText={AssistantText}
              MessageActions={ProjectMessageActions}
            />
          ) : (
            <div className="mb-5">
              <AssistantText text={openingAssistantMessage} streaming={false} />
            </div>
          )
        ) : null}
        {timelineLeadingContent}
      </>
    );
  }, [
    openingAssistantMessage,
    openingAssistantMessageFeedback,
    timelineLeadingContent,
    pid,
    AssistantText,
    ProjectMessageActions,
  ]);

  const renderCompletedTurnFooter = useCallback<
    NonNullable<ProjectChatTimelineSlotProps['renderCompletedTurnFooter']>
  >(
    (turn) => {
      if (!completedTurnHasFeedbackTarget(turn.items)) return null;
      const trailingEvidenceToolCallId = findTrailingEvidenceChildToolCallId(turn.items);
      if (
        trailingEvidenceToolCallId &&
        inlineReplyActions?.has(trailingEvidenceToolCallId)
      ) {
        return null;
      }
      const messageId = resolveCompletedTurnMessageId(
        transcriptIndexById,
        turn.items,
        turn.nextTurnFirstItem
      );
      if (messageId === null) return null;
      return (
        <ProjectReplyMessageActions
          projectId={pid}
          displayItems={turn.items}
          messageId={messageId}
          givenRatings={givenReplyRatings}
          visibility={EARLIER_RESPONSE_ACTIONS_VISIBILITY}
        />
      );
    },
    [
      givenReplyRatings,
      inlineReplyActions,
      pid,
      transcriptIndexById,
      ProjectReplyMessageActions,
    ]
  );

  const timelineModel = {
    ...conversationTimeline,
    tailContent: (
      <>
        {conversationTimeline.tailContent}
        {showLatestReplyActions && !latestInlineReplyToolCallId ? (
          <ProjectReplyMessageActions
            projectId={pid}
            displayItems={conversationTimeline.displayItems}
            messageId={latestReplyMessageId}
            givenRatings={givenReplyRatings}
            hideFeedback={showReplyCopyOnly}
          />
        ) : null}
        {credit.transcriptTrailing}
        {showFollowUpQuestions ? (
          <ProjectReplyFollowUpQuestions
            projectId={pid}
            replyId={followUpReplyId}
            onSelect={handleFollowUpSelect}
          />
        ) : null}
        {showContinueOnMobileCard ? (
          <ContinueOnMobileCard
            onGetApp={openGetMobileApp}
            onTrack={trackMobileHandoffCard}
          />
        ) : null}
      </>
    ),
  };

  return (
    <ContinueAfterConnectProvider
      onContinue={(connectorId) => {
        composerRef.current?.continueAfterConnect(connectorId);
      }}
    >
      {/* Deliberately no background: the chat inherits its host — white
          inside the routines detail card (side-by-side), sand once the pane
          flattens onto the page (artifact open / standalone run / home). */}
      <div
        className={cn(
          'relative flex min-h-0 min-w-0 flex-col',
          provisional && centerProvisionalContent ? 'h-auto' : 'h-full',
          transcriptTextScale === 'p1' &&
            '[&_[data-project-chat-transcript-text]]:text-base'
        )}
        data-testid="project-chat-shell"
      >
        {fileDropScope ? (
          <FileDropSurface
            scope="container"
            container={fileDropScope === 'panel' ? fileDropBoundaryRef : fileDropContent}
            label={intl.formatMessage({
              defaultMessage: 'Drop files in chat',
              id: 'AaAy7OAEdC',
              description: 'Label shown over the full-surface chat file-drop overlay',
            })}
            disabled={runClosed || readOnly || lockMutations}
            accept={PROJECT_ATTACHMENTS_ACCEPT_FORMATS}
            multiple
            normalizeFile={normalizePastedFile}
            onFilesAccepted={handleChatFilesAccepted}
            onFilesRejected={handleChatFilesRejected}
            data-testid="project-chat-drop-takeover"
          />
        ) : null}
        <YourResidentDrawer
          items={items}
          liveDelegateStep={viewerLiveDelegateStep}
          host={residentDrawer}
        />
        {isGrantRevoked ? (
          <WorkChatNotFound reason="revoked" />
        ) : (
          <>
            <ProjectConversationTimeline
              model={timelineModel}
              renderCompletedTurnFooter={
                showEarlierReplyActions ? renderCompletedTurnFooter : undefined
              }
              leadingContent={leadingContent}
              topFadeClassName={timelineTopFadeClassName}
              fadeColorClassName={timelineFadeColorClassName}
              scrollClassName={cn(
                'scrollbar-fade overflow-y-auto [scrollbar-gutter:stable_both-edges]',
                composerExpanded ? 'hidden' : 'flex-1'
              )}
              scrollContentClassName={cn(
                'mx-auto flex w-full max-w-3xl flex-col gap-5 px-6 pt-6',
                provisional &&
                  timelineModel.isEmpty &&
                  !centerProvisionalContent &&
                  'h-full min-h-full'
              )}
              scrollTestId="project-chat-scroll"
              onScrollPointerDown={handleTranscriptPointerDown}
              contentTestId="project-chat-content"
              emptyFallback={
                provisional
                  ? (emptyState ?? timelineModel.tailContent)
                  : timelineModel.tailContent
              }
              timelineProps={{
                agentRunId,
                projectId: pid,
                viewedDocument: transport.viewedDocument,
                onOpenArtifact,
                onResolveApproval: lockMutations ? undefined : transport.resolveApproval,
                liveDelegateStep: viewerLiveDelegateStep,
                rendererOptions: {
                  resolvingApproval: transport.resolvingApproval,
                  requireComputerUsePermissions: () => ensurePermissions('computer_use'),
                  desktopTargetEnabled: transport.desktopTargetEnabled,
                  projectTitle,
                  routineRunWorkflowId: runRoutine?.workflow_id,
                  turnInFlight,
                  inlineReplyActions,
                  loaderPhrases: contextualLoaderPhrases,
                },
              }}
            />

            {/* Composer dock — no top divider so the dock blends into
          the surface; the rounded composer shell + the queue stack
          above it provide enough visual separation from the
          transcript without a hairline. shrink-0 keeps it pinned while
          the transcript alone scrolls (routine embeds + short viewports). */}
            {inspectReadOnly ? (
              readOnlyFooter
            ) : (
              <div
                className={cn(
                  'group/landing relative min-w-0',
                  composerExpanded ? 'flex min-h-0 flex-1 items-center' : 'shrink-0'
                )}
                data-testid={
                  composerSessionAppearance?.kind === 'landing' ||
                  composerSessionAppearance?.kind === 'focused'
                    ? 'work-home-composer-dock'
                    : undefined
                }
              >
                {composerBackdrop}
                <ProjectChatComposer
                  ref={composerRef}
                  isSubmitting={composerIsSubmitting}
                  prefilledValue={prefilledComposerPrompt ?? prefilledPrompt}
                  toolbarLeading={composerToolbarLeading}
                  speedMenu={composerSpeedMenu}
                  composerMode={composerMode}
                  onComposerModeChange={onComposerModeChange}
                  placeholderPreview={composerPlaceholderPreview}
                  sessionAppearance={composerSessionAppearance}
                  patientScope={composerPatientScope}
                  slashSectionOrder={composerSlashSectionOrder}
                  sessionShortcutFlyout={composerSessionShortcutFlyout}
                  layout={composerLayout}
                  toolbarSourcesSlot={resolvedToolbarSourcesSlot}
                  evidenceSourcesSlot={composerEvidenceSourcesSlot}
                  sessionAutoFocus={composerAutoFocus}
                  onSessionFocusChange={onComposerFocusChange}
                  onSessionDraftEmptyChange={onComposerDraftEmptyChange}
                  expanded={composerExpanded}
                  onExpandedChange={onComposerExpandedChange}
                  composerDockLeading={composerDockLeading ?? credit.composerDockLeading}
                  sendDisabled={
                    !projectId && composerSessionAppearance?.kind === 'focused'
                      ? false
                      : credit.sendDisabled
                  }
                  composerBanner={
                    shareReadOnly ? (
                      <SharedProjectBanner
                        ownerDisplayName={ownerDisplayName}
                        onContinue={onContinueSharedProject}
                      />
                    ) : computerUseBannerKind === 'teach' ? (
                      <ComputerUseNudgeCard
                        downloadHref={DESKTOP_APP_DOWNLOAD_URL}
                        onDismiss={dismissComputerUseNudge}
                        onRemindLater={dismissComputerUseNudge}
                        onTrack={trackWorkRpaDesktopRequiredShown}
                      />
                    ) : computerUseBannerKind === 'retry' ? (
                      <ComputerUseNudgeCard
                        variant="retry"
                        downloadHref={DESKTOP_APP_DOWNLOAD_URL}
                        onDismiss={dismissComputerUseNudge}
                        onRetry={retryComputerUseFromDesktop}
                        onTrack={trackWorkRpaDesktopRequiredShown}
                      />
                    ) : undefined
                  }
                  activeClarificationApproval={activeClarificationApproval}
                  cancelling={cancelling}
                  cancelTurn={stopTurn}
                  clearLinkedItems={clearLinkedItems}
                  discardedTurnNonce={discardedTurnNonce}
                  discardedTurn={discardedTurn}
                  hasPendingApproval={hasPendingApproval}
                  linkedItems={linkedItems}
                  runClosed={runClosed}
                  readOnly={shareReadOnly}
                  runFailed={runFailed}
                  startTurn={startTurnWithScrollSnap}
                  approvalMode={transport.approvalMode}
                  onApprovalModeChange={transport.setApprovalMode}
                  turnInFlight={turnInFlight}
                  isStartingSeededTurn={isStartingSeededTurn}
                  wrappingUp={wrappingUp}
                  analyticsSurface={analyticsSurface}
                  analyticsClinicalSessionId={analyticsClinicalSessionId}
                  projectId={pid || undefined}
                  recordingEligible={provisional || recordingEligible}
                  desktopTargetEnabled={transport.desktopTargetEnabled}
                  draftStorageScope={draftStorageScope}
                  hostDraft={hostDraft}
                  contextLock={contextLock}
                  onScrollTranscript={scrollTranscript}
                  onHasQueuedTurnsChange={handleHasQueuedTurnsChange}
                  onSteerPendingApprovals={steerPendingApprovals}
                  disableFileDrop={
                    fileDropScope === 'panel' ||
                    (fileDropScope === 'window' && fileDropContent != null)
                  }
                />
              </div>
            )}

            {!lockMutations && shouldShowWorkFeedbackNudge && !isMobile ? (
              <ExperienceFeedbackNudge
                easeRating={workFeedbackEaseRating}
                usefulnessRating={workFeedbackUsefulnessRating}
                detail={workFeedbackDetail}
                isRatingSubmitted={isWorkFeedbackRatingSubmitted}
                onEaseRatingSubmit={handleWorkFeedbackEaseRatingSubmit}
                onUsefulnessRatingSubmit={handleWorkFeedbackUsefulnessRatingSubmit}
                onDetailChange={setWorkFeedbackDetail}
                onSubmit={() => {
                  void handleWorkFeedbackSubmit();
                }}
                onDismiss={handleWorkFeedbackDismiss}
                isSubmitDisabled={isWorkFeedbackSubmitDisabled}
                labels={workFeedbackLabels}
                deferCommentUntilUsefulness
              />
            ) : null}
          </>
        )}
      </div>
    </ContinueAfterConnectProvider>
  );
};
